import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box } from '@rsos/components/capstone/base';
import {
  useLazyGetConversationMetadataQuery,
  useLazyCreateConversationQuery,
  useLazyEndConversationQuery,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Api';
import {
  useSelectedTextFrom911ConversationID,
  useSelectedTextFrom911Conversation,
} from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Selectors';
import { TF911_CONVERSATION_ENDED } from '@rsos/reduxDataStore/src/features/textFrom911/textFrom911Slice';
import { selectUserOrgByApp } from '@rsos/sinatra';
import { TF911Container } from './TF911.styles';
import TF911Chat from './TF911Chat';
import TF911ChatHeader from './TF911ChatHeader';

export const TF911_CHAT_HEADER_HEIGHT = 101;

const TF911ChatBox = ({
  bothChatsEnabled,
  isTextFrom911ChatBoxVisible,
  onCollapseChat,
}) => {
  const [showPreconfiguredMessages, setShowPreconfiguredMessages] = useState(
    false,
  );

  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );

  const orgName = useSelector(
    state =>
      selectUserOrgByApp(
        state.sinatra,
        state.sinatra.user.currentRole.application,
      ).name,
  );

  const conversationID = useSelectedTextFrom911ConversationID(selectedCallerID);

  const conversation = useSelectedTextFrom911Conversation(selectedCallerID);

  const isConversationEnded = useMemo(
    () => conversation?.data?.custom?.STATUS === TF911_CONVERSATION_ENDED,
    [conversation],
  );

  const [
    triggerGetConversationMetaData,
    getConversationMetaDataResult,
  ] = useLazyGetConversationMetadataQuery();

  const [triggerCreateConversation] = useLazyCreateConversationQuery();

  const [triggerEndConversation] = useLazyEndConversationQuery();

  useEffect(
    () => {
      if (selectedCallerID) {
        triggerGetConversationMetaData({
          callerID: selectedCallerID,
          orgName,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedCallerID],
  );

  const handleActionButtonClick = () => {
    onCollapseChat('tf911');
  };

  const handleClickShowAccordion = useCallback(() => {
    setShowPreconfiguredMessages(prev => !prev);
  }, []);

  const handleEndConversation = useCallback(
    callerID => {
      triggerEndConversation({ callerID, orgName });
    },
    [orgName, triggerEndConversation],
  );

  const handleStartConversation = useCallback(
    message => {
      triggerCreateConversation({
        callerID: selectedCallerID,
        orgName,
        startMessage: message,
      }).then(response => {
        if (response.isSuccess) {
          setShowPreconfiguredMessages(false);
        }
      });
    },
    [orgName, selectedCallerID, triggerCreateConversation],
  );

  const showStartChat = useMemo(() => {
    return !conversationID || isConversationEnded;
  }, [conversationID, isConversationEnded]);

  const chatHeight = useMemo(() => {
    if (isTextFrom911ChatBoxVisible) {
      if (bothChatsEnabled) {
        return '50%';
      }
      return '100%';
    }
    return TF911_CHAT_HEADER_HEIGHT;
  }, [isTextFrom911ChatBoxVisible, bothChatsEnabled]);

  const containerHeight = useMemo(() => {
    // Chat has been started and not collapsed or chat has not been started and
    // the preconfigured messages accordion is shown
    if (
      (isTextFrom911ChatBoxVisible && !showStartChat) ||
      showPreconfiguredMessages ||
      conversationID
    ) {
      return '100%';
    } else {
      return `${TF911_CHAT_HEADER_HEIGHT}px`;
    }
  }, [
    isTextFrom911ChatBoxVisible,
    showStartChat,
    showPreconfiguredMessages,
    conversationID,
  ]);

  return (
    <TF911Container
      data-name="text-from-911-container"
      height={chatHeight}
      maxHeight={containerHeight}
    >
      <TF911ChatHeader
        isChatBoxVisible={isTextFrom911ChatBoxVisible}
        onActionButtonClick={handleActionButtonClick}
        onCollapseChat={onCollapseChat}
        onClickShowAccordion={handleClickShowAccordion}
        onEndConversation={handleEndConversation}
        onStartConversation={handleStartConversation}
        showPreconfiguredMessages={showPreconfiguredMessages}
        showStartChat={showStartChat}
      />

      {getConversationMetaDataResult.status === 'fulfilled' && (
        <TF911Chat
          callerID={selectedCallerID}
          showPreconfiguredMessages={showPreconfiguredMessages}
          showStartChat={showStartChat}
          isTextFrom911ChatBoxVisible={isTextFrom911ChatBoxVisible}
        />
      )}
    </TF911Container>
  );
};

export default TF911ChatBox;
